import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { EnergyLogo, EnergyLogoStacked } from '../components/EnergyLogo';
import { Mail, Lock, User, Phone, ArrowLeft, FileText, Building2, Briefcase, Eye, EyeOff, MapPin, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { sediApi } from '../lib/api';

const API_URL = '';

const Login = () => {
  const { login, register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const tipoAccesso = searchParams.get('tipo') || 'cliente'; // 'cliente' o 'operatore'
  const isOperatore = tipoAccesso === 'operatore';
  const redirectTarget = searchParams.get('redirect') || null;

  // Valida che il redirect sia relativo (no phishing verso URL esterni)
  const safeRedirect = redirectTarget && redirectTarget.startsWith('/') && !redirectTarget.startsWith('//')
    ? redirectTarget
    : null;
  
  // Distingui tra codice sede e codice referral utente
  const isUserReferral = refCode && refCode.startsWith('REF');
  const sedeCode = isUserReferral ? '' : (refCode || '');
  const userReferralCode = isUserReferral ? refCode : '';
  
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  /**
   * Pre-warm dashboard chunks mentre l'utente legge/digita.
   * Dynamic `import()` istruisce webpack a scaricare il chunk in background
   * (la promise viene scartata: ci interessa solo il "fetch + parse" lato browser).
   * Quando il login va a buon fine, la rotta destinazione è già pronta → zero spinner.
   */
  useEffect(() => {
    // Differiamo di 600ms per non interferire con il TTFB del paint critico della Login.
    const t = setTimeout(() => {
      if (isOperatore) {
        // Operatore/Admin → CRM
        import('./AdminDashboard');
      } else {
        // Cliente → Dashboard
        import('./ClienteDashboard');
      }
    }, 600);
    return () => clearTimeout(t);
  }, [isOperatore]);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    cap: '',
    sede_id: '',
    sede_code: sedeCode || '',
    referral_code: userReferralCode || '',
  });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [availableSedi, setAvailableSedi] = useState([]);
  const [loadingSedi, setLoadingSedi] = useState(false);
  const [mainSede, setMainSede] = useState(null); // Sede principale come fallback

  // Carica SEMPRE le sedi all'avvio per avere sempre la sede principale come fallback
  useEffect(() => {
    const loadInitialSedi = async () => {
      try {
        const data = await sediApi.getSediByCAP(null);
        if (data.sedi && data.sedi.length > 0) {
          setAvailableSedi(data.sedi);
          // Salva la sede principale per fallback
          const main = data.sedi.find(s => s.is_main);
          setMainSede(main || data.sedi[0]);
          // Pre-seleziona la sede suggerita (principale)
          if (data.suggested_sede_id) {
            setRegisterData(prev => ({ ...prev, sede_id: data.suggested_sede_id }));
          }
        }
      } catch (error) {
        console.error('Errore caricamento sedi iniziale:', error);
      }
    };
    loadInitialSedi();
  }, []);

  // Aggiorna la selezione quando cambia il CAP (solo se CAP completo)
  useEffect(() => {
    const loadSediByCAP = async () => {
      // Solo se il CAP ha esattamente 5 caratteri
      if (registerData.cap.length === 5) {
        setLoadingSedi(true);
        try {
          const data = await sediApi.getSediByCAP(registerData.cap);
          if (data.sedi && data.sedi.length > 0) {
            setAvailableSedi(data.sedi);
            // Se c'è una sede suggerita, aggiorna la selezione
            if (data.suggested_sede_id) {
              setRegisterData(prev => ({ ...prev, sede_id: data.suggested_sede_id }));
            }
          }
          // Se l'API non restituisce sedi, usa il fallback della sede principale
          else if (mainSede) {
            setAvailableSedi([mainSede]);
            setRegisterData(prev => ({ ...prev, sede_id: mainSede.sede_id }));
          }
        } catch (error) {
          console.error('Errore caricamento sedi per CAP:', error);
          // In caso di errore, usa il fallback della sede principale
          if (mainSede) {
            setAvailableSedi([mainSede]);
            setRegisterData(prev => ({ ...prev, sede_id: mainSede.sede_id }));
          }
        } finally {
          setLoadingSedi(false);
        }
      }
    };
    
    // Debounce la chiamata API
    const timer = setTimeout(loadSediByCAP, 300);
    return () => clearTimeout(timer);
  }, [registerData.cap, mainSede]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(loginData.email, loginData.password);
      toast.success('Accesso effettuato!');
      // Redirect prioritario: query param ?redirect > default per ruolo
      if (safeRedirect) {
        navigate(safeRedirect);
      } else if (user.role === 'super_admin' || user.role === 'admin_sede' || user.role === 'operatore') {
        navigate('/crm');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.phone) {
      toast.error('Il numero di telefono è obbligatorio');
      return;
    }
    
    // Verifica sede selezionata - usa fallback a sede principale se necessario
    let sedeIdToUse = registerData.sede_id;
    if (!sedeCode && !sedeIdToUse) {
      // Prova a usare la sede principale come fallback
      if (mainSede) {
        sedeIdToUse = mainSede.sede_id;
        setRegisterData(prev => ({ ...prev, sede_id: mainSede.sede_id }));
      } else if (availableSedi.length === 0) {
        toast.error('Nessun ufficio operativo disponibile. Contatta l\'assistenza.');
        return;
      } else {
        toast.error('Seleziona un ufficio di riferimento');
        return;
      }
    }
    
    if (!privacyConsent) {
      toast.error('Devi accettare l\'informativa privacy per registrarti');
      return;
    }
    setIsLoading(true);
    try {
      const user = await register({ 
        ...registerData, 
        privacy_consent: true,
        marketing_consent: marketingConsent 
      });
      toast.success('Registrazione completata!');
      navigate(safeRedirect || '/dashboard'); // Area Clienti
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    googleLogin(sedeCode);
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <EnergyLogoStacked size="md" />
            </div>
            <CardTitle className="text-2xl text-white flex items-center justify-center gap-2">
              {isOperatore ? (
                <>
                  <Briefcase className="w-5 h-5 text-cyan-400" />
                  Area Operatori
                </>
              ) : (
                'Area Clienti'
              )}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {isOperatore ? (
                <>
                  Accesso riservato ad agenzie, freelance e banconisti
                  <span className="block mt-2 text-xs text-slate-500">
                    Sei un cliente? <Link to="/login?tipo=cliente" className="text-cyan-400 hover:underline">Accedi qui</Link>
                  </span>
                </>
              ) : (
                <>
                  Accedi o registrati per continuare
                  {sedeCode && (
                    <span className="block mt-2 text-cyan-400">
                      Codice sede: {sedeCode}
                    </span>
                  )}
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              {/* Per operatori mostra solo login, per clienti mostra entrambi */}
              {isOperatore ? (
                <div className="mb-6 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="text-sm text-slate-400 text-center">
                    <Briefcase className="w-4 h-4 inline mr-1" />
                    Accesso riservato agli operatori autorizzati
                  </p>
                </div>
              ) : (
                <TabsList className="grid w-full grid-cols-2 bg-slate-800">
                  <TabsTrigger value="login" data-testid="login-tab">Accedi</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Registrati</TabsTrigger>
                </TabsList>
              )}
              
              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="nome@esempio.it"
                        className="pl-10 bg-slate-950 border-slate-800 text-white"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10 bg-slate-950 border-slate-800 text-white"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        data-testid="toggle-login-password"
                      >
                        {showLoginPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-gradient"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? 'Accesso...' : 'Accedi'}
                  </Button>

                  {/* Google Login - Solo per clienti */}
                  {!isOperatore && (
                    <>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-800"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-slate-900 text-slate-500">oppure</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                        onClick={handleGoogleLogin}
                        data-testid="google-login-btn"
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continua con Google
                      </Button>
                    </>
                  )}
                </form>
              </TabsContent>
              
              {/* Tab registrazione solo per clienti */}
              {!isOperatore && (
              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* Referral Banner */}
                  {userReferralCode && (
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 mb-4">
                      <div className="flex items-center gap-2">
                        <Gift className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="text-sm text-emerald-300 font-medium">Invitato da un amico!</p>
                          <p className="text-xs text-slate-400">Registrandoti potrai ricevere cashback esclusivi</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-slate-300">Nome completo</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Mario Rossi"
                        className="pl-10 bg-slate-950 border-slate-800 text-white"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-slate-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="nome@esempio.it"
                        className="pl-10 bg-slate-950 border-slate-800 text-white"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-phone" className="text-slate-300">Telefono *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="register-phone"
                        type="tel"
                        placeholder="+39 333 1234567"
                        className="pl-10 bg-slate-950 border-slate-800 text-white"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        required
                        data-testid="register-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="register-password"
                        type={showRegisterPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10 bg-slate-950 border-slate-800 text-white"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        minLength={6}
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        data-testid="toggle-register-password"
                      >
                        {showRegisterPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* CAP e Selezione Ufficio - solo se non c'è già un sedeCode dal link */}
                  {!sedeCode && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="register-cap" className="text-slate-300">CAP (Codice Postale)</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            id="register-cap"
                            type="text"
                            placeholder="00100"
                            maxLength={5}
                            className="pl-10 bg-slate-950 border-slate-800 text-white"
                            value={registerData.cap}
                            onChange={(e) => setRegisterData({ ...registerData, cap: e.target.value.replace(/\D/g, '') })}
                            data-testid="register-cap-input"
                          />
                        </div>
                        <p className="text-xs text-slate-500">Inserisci il CAP per trovare l'ufficio più vicino</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-sede" className="text-slate-300">Ufficio di riferimento *</Label>
                        <Select 
                          value={registerData.sede_id} 
                          onValueChange={(value) => setRegisterData({ ...registerData, sede_id: value })}
                          disabled={availableSedi.length === 0}
                        >
                          <SelectTrigger 
                            className="bg-slate-950 border-slate-800 text-white"
                            data-testid="register-sede-select"
                          >
                            <SelectValue placeholder={
                              loadingSedi 
                                ? "Caricamento uffici..." 
                                : availableSedi.length === 0 
                                  ? "Nessun ufficio disponibile" 
                                  : "Seleziona un ufficio"
                            } />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                            {availableSedi.map((sede) => (
                              <SelectItem 
                                key={sede.sede_id} 
                                value={sede.sede_id}
                                className="text-white hover:bg-slate-800"
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-cyan-400" />
                                  <span>{sede.name}</span>
                                  {sede.city && <span className="text-slate-400 text-xs">({sede.city})</span>}
                                  {sede.is_main && (
                                    <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
                                      Principale
                                    </span>
                                  )}
                                  {sede.is_suggested && !sede.is_main && (
                                    <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">
                                      Consigliato
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                            {availableSedi.length === 0 && !loadingSedi && (
                              <div className="px-3 py-2 text-slate-400 text-sm text-center">
                                Caricamento sedi in corso...
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                        {availableSedi.length > 0 && (
                          <p className="text-xs text-slate-500">
                            {availableSedi.some(s => s.is_suggested && !s.is_main) 
                              ? 'Sede consigliata in base al tuo CAP'
                              : availableSedi.some(s => s.is_main)
                                ? 'Sede principale selezionata automaticamente'
                                : 'Tutte le tue pratiche saranno gestite da questo ufficio'
                            }
                          </p>
                        )}
                        {availableSedi.length === 0 && !loadingSedi && !mainSede && (
                          <p className="text-xs text-amber-400">
                            Nessun ufficio operativo censito. Contatta l'assistenza.
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  
                  {sedeCode && (
                    <p className="text-sm text-cyan-400">
                      Ti registrerai come cliente della sede: {sedeCode}
                    </p>
                  )}
                  
                  {/* Privacy Consent Checkbox */}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                    <Checkbox
                      id="privacy-consent"
                      checked={privacyConsent}
                      onCheckedChange={setPrivacyConsent}
                      className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                      data-testid="privacy-consent-checkbox"
                    />
                    <div className="space-y-1">
                      <Label 
                        htmlFor="privacy-consent" 
                        className="text-sm text-slate-300 cursor-pointer leading-relaxed"
                      >
                        Autorizzo il trattamento dei miei dati personali e acconsento alla lavorazione delle pratiche con i miei documenti ai sensi del GDPR (Reg. UE 2016/679) *
                      </Label>
                      <a 
                        href={`${API_URL}/api/privacy/informativa`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                        data-testid="privacy-download-link"
                      >
                        <FileText className="w-3 h-3" />
                        Scarica l'informativa completa
                      </a>
                    </div>
                  </div>
                  
                  {/* Marketing Communications Consent Checkbox */}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                    <Checkbox
                      id="marketing-consent"
                      checked={marketingConsent}
                      onCheckedChange={setMarketingConsent}
                      className="mt-0.5 border-slate-600 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      data-testid="marketing-consent-checkbox"
                    />
                    <Label 
                      htmlFor="marketing-consent" 
                      className="text-sm text-slate-400 cursor-pointer leading-relaxed"
                    >
                      Acconsento a ricevere comunicazioni commerciali esclusivamente dal mio ufficio di fiducia per essere informato su nuove opportunità e servizi utili per le mie esigenze future
                    </Label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-gradient"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? 'Registrazione...' : 'Registrati'}
                  </Button>
                </form>
              </TabsContent>
              )}
            </Tabs>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
